import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import useLang from '../hooks/useLang';
import useAuthStore from '../stores/authStore';

const fmt = v => new Intl.NumberFormat('sw-TZ').format(v || 0);

export default function ChangiwaDetail() {
  const { T } = useLang();
  const { id } = useParams();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get(`/changiwa-events/${id}`).then(r => setEvent(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const handlePay = async (paymentId) => {
    await api.post(`/changiwa-payments/${paymentId}/pay`);
    load();
  };

  const handleWaive = async (paymentId) => {
    await api.post(`/changiwa-payments/${paymentId}/waive`);
    load();
  };

  const handlePayAll = async () => {
    if (!confirm('Weka wote kama wamelipa?')) return;
    await api.post(`/changiwa-events/${id}/pay-all`);
    load();
  };

  const [cuttingId, setCuttingId] = useState(null);
  const handleCutNow = async (paymentId) => {
    setCuttingId(paymentId);
    try {
      await api.post(`/changiwa-payments/${paymentId}/cut-now`);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Bado hana mchango wa kutosha kukata');
    } finally {
      setCuttingId(null);
    }
  };

  const handleDisburse = async () => {
    const defaultAmt = event.total_collected > 0 ? event.total_collected : (event.amount_per_member * event.total_members);
    const input = window.prompt(
      `Lipa kiasi gani kwa mhusika (${event.beneficiary_name || 'mhusika'}) kutoka kwenye fedha za kikundi?\n\nFedha zilizokusanywa: ${new Intl.NumberFormat('sw-TZ').format(defaultAmt)} TZS`,
      defaultAmt
    );
    if (!input) return;
    const amount = parseFloat(input);
    if (isNaN(amount) || amount <= 0) return alert('Kiasi si sahihi');
    try {
      const { data } = await api.post(`/changiwa-events/${id}/disburse`, { amount });
      alert(data.message);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Imeshindwa');
    }
  };

  if (loading) return <div className="text-center py-20 text-gray-500">{T.common.loading}</div>;
  if (!event) return <div className="text-center py-20 text-red-500">{T.changiwaDetail.notFound}</div>;

  const pending = event.payments?.filter(p => p.status === 'pending') || [];
  const paid    = event.payments?.filter(p => p.status === 'paid') || [];
  const waived  = event.payments?.filter(p => p.status === 'waived') || [];
  const pct     = event.total_members > 0 ? Math.round((event.paid_count / event.total_members) * 100) : 0;

  return (
    <div className="max-w-2xl space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/changiwa" className="text-green-700 hover:text-green-900 text-sm">← {T.changiwaDetail.back}</Link>
        <span className="text-gray-400">/</span>
        <h1 className="text-xl font-bold text-gray-800 truncate">{event.title}</h1>
      </div>

      {/* Summary card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <span className="px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-600 font-medium">{event.type_name}</span>
            {event.beneficiary_name && <p className="text-sm text-gray-500 mt-1">{T.changiwaDetail.beneficiary}: <strong>{event.beneficiary_name}</strong></p>}
            {event.event_date && <p className="text-xs text-gray-400 mt-0.5">{new Date(event.event_date).toLocaleDateString('sw-TZ')}</p>}
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-green-700">{fmt(event.total_collected)} TZS</div>
            <div className="text-xs text-gray-400">{event.paid_count}/{event.total_members} {T.changiwaDetail.collected}</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{pct}% {T.changiwaDetail.progress}</span>
            <span>{T.changiwaDetail.expected}: {fmt(event.amount_per_member * event.total_members)} TZS</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {/* Disbursement status */}
        {+event.disbursed_amount > 0 ? (
          <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <div>
              <span className="text-sm font-semibold text-orange-800">💸 {T.changiwaDetail.disbursed}</span>
              <p className="text-xs text-orange-600 mt-0.5">
                {event.disbursed_at ? new Date(event.disbursed_at).toLocaleDateString('sw-TZ') : ''} — {T.changiwaDetail.disbursedAt}
              </p>
            </div>
            <span className="text-lg font-bold text-orange-700">
              {fmt(event.disbursed_amount)} TZS
            </span>
          </div>
        ) : isAdmin ? (
          <div className="mt-4 flex gap-2">
            {event.status === 'open' && pending.length > 0 && (
              <button onClick={handlePayAll}
                className="flex-1 bg-green-700 hover:bg-green-800 text-white py-2 rounded-lg text-sm font-medium">
                ✓ {T.changiwaDetail.payAll} ({pending.length})
              </button>
            )}
            <button onClick={handleDisburse}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2 rounded-lg text-sm font-medium">
              💸 {T.changiwaDetail.disburse} {event.beneficiary_name || T.changiwa.beneficiary}
            </button>
          </div>
        ) : null}
      </div>

      {/* Pending */}
      {pending.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-red-600 mb-2">❌ {T.changiwaDetail.pending} ({pending.length})</h2>
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {pending.map(p => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3">
                <div>
                  <span className="font-medium text-gray-800 text-sm">{p.member_name}</span>
                  <span className="text-xs text-gray-400 ml-2">{p.member_phone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-red-500">{fmt(p.amount)} TZS</span>
                  {isAdmin && event.status === 'open' && (
                    <div className="flex gap-1">
                      <button onClick={() => handleCutNow(p.id)} disabled={cuttingId === p.id}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded-lg text-xs font-medium disabled:opacity-60">
                        {cuttingId === p.id ? '...' : '✂️ Kata Sasa'}
                      </button>
                      <button onClick={() => handlePay(p.id)}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-xs font-medium">
                        {T.changiwaDetail.pay}
                      </button>
                      <button onClick={() => handleWaive(p.id)}
                        className="border border-gray-300 text-gray-500 px-2 py-1 rounded-lg text-xs hover:bg-gray-50">
                        {T.changiwaDetail.waive}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Paid */}
      {paid.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-green-600 mb-2">✅ {T.changiwaDetail.paidSection} ({paid.length})</h2>
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {paid.map(p => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3">
                <span className="font-medium text-gray-700 text-sm">{p.member_name}</span>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-green-600">{fmt(p.amount)} TZS</span>
                  {p.paid_at && <span className="text-xs text-gray-400">{new Date(p.paid_at).toLocaleDateString('sw-TZ')}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Waived */}
      {waived.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-400 mb-2">⊘ {T.changiwaDetail.waived} ({waived.length})</h2>
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {waived.map(p => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-gray-500 line-through">{p.member_name}</span>
                <span className="text-xs text-gray-400">{fmt(p.amount)} TZS</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
